import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import { BrowserRouter } from 'react-router-dom'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { MsalProvider } from '@azure/msal-react'
import { msalInstance } from './api/msal'
import { AuthProvider } from './features/auth/AuthProvider'
import { AppRouter } from './routes/AppRouter'
import { initAppearance } from './features/settings/AppearancePanel'
import './app.css'

initAppearance()

const queryClient = new QueryClient({
    defaultOptions: {
        queries: {
            staleTime: 5_000,
            retry: 1,
        },
    },
})

const root = document.getElementById('root')
if (!root) throw new Error('No #root element found in index.html.')

// MSAL v3+ must be initialized before use, and any redirect sign-in must be
// processed before the app renders. Set the active account (on reload or after
// a redirect) so silent token acquisition has an account to work with.
//
// Boot NEVER fails closed: a rejected initialize/handleRedirectPromise (a
// redirect that came back carrying an error, a corrupt cache) must still land
// the user on the sign-in screen, not a blank page they can only escape by
// clearing site data. AuthProvider re-derives session state from scratch.
const boot = async () => {
    try {
        await msalInstance.initialize()
        const redirectResult = await msalInstance.handleRedirectPromise()
        if (redirectResult?.account) {
            msalInstance.setActiveAccount(redirectResult.account)
        } else {
            const accounts = msalInstance.getAllAccounts()
            if (accounts.length > 0) {
                msalInstance.setActiveAccount(accounts[0])
            }
        }
    } catch {
        // Render anyway — the sign-in screen is the recovery path.
    }

    createRoot(root).render(
        <StrictMode>
            <MsalProvider instance={msalInstance}>
                <BrowserRouter>
                    <QueryClientProvider client={queryClient}>
                        <AuthProvider>
                            <AppRouter />
                        </AuthProvider>
                    </QueryClientProvider>
                </BrowserRouter>
            </MsalProvider>
        </StrictMode>,
    )
}

void boot()
